import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Form, Button, Table, Row, Col, Pagination, Alert, Nav, Modal, Badge } from 'react-bootstrap';
import { FaPlus, FaUpload, FaSearch, FaSort, FaArrowLeft, FaTrash, FaUserPlus, FaSync, FaFilter, FaTimes, FaFileExcel, FaEye, FaEdit } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import AssetForm from './AssetForm';
import AssetAssignmentModal from './AssetAssignmentModal';
import { assetService } from '../services/assetService';
import { STATUS_OF_EQUIPMENT_OPTIONS, BASE_URL } from '../../Constants/constants';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Constants – these are the raw backend values, NOT translated strings.
// Comparing against t('available') etc. breaks when the UI language changes.
// ---------------------------------------------------------------------------
const ASSIGNABLE_STATUSES = ['Available', 'In Stock', 'Not Used', 'Ready'];

// Maps raw backend status → Bootstrap badge variant
const STATUS_BADGE_VARIANT = {
    'Available':     'success',
    'In Stock':      'success',
    'Not Used':      'success',
    'Ready':         'success',
    'Assigned':      'warning',
    'In Use':        'primary',
    'Under Repair':  'danger',
    // DB typo variants — same colour as Under Repair
    'In Reperation': 'danger',
    'In Reparation': 'danger',
    'Retired':       'secondary',
    'Lost':          'dark',
    'Maintenance':   'danger',
    'Disposed':      'secondary',
};

const getStatusBadgeVariant = (status) => STATUS_BADGE_VARIANT[status] || 'secondary';

// Maps raw backend status → i18n key.
// Handles known DB typos (e.g. "In Reperation") so they never render raw.
const getStatusTranslationKey = (status) => {
    const statusMap = {
        'Available':      'assets.statusAvailable',
        'In Use':         'assets.statusInUse',
        'Under Repair':   'assets.statusUnderRepair',
        // DB typo variant — treat identically to "Under Repair"
        'In Reperation':  'assets.statusUnderRepair',
        'In Reparation':  'assets.statusUnderRepair',
        'Retired':        'assets.statusRetired',
        'Lost':           'assets.statusLost',
        'Assigned':       'assets.statusAssigned',
        'In Stock':       'assets.statusInStock',
        'Not Used':       'assets.statusNotUsed',
        'Ready':          'assets.statusReady',
        'Maintenance':    'assets.statusMaintenance',
        'Disposed':       'assets.statusDisposed',
    };
    return statusMap[status] ?? status;
};

// ---------------------------------------------------------------------------
// Auth helper
// ---------------------------------------------------------------------------
const getAuthConfig = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) throw new Error('Authentication token is missing');
    return { headers: { Authorization: `Bearer ${token}` } };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AssetPage = () => {
    const { t } = useTranslation('assets');

    // View mode
    const [viewMode, setViewMode]       = useState('assets');
    const [users, setUsers]             = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userAssets, setUserAssets]   = useState([]);

    // Asset list state
    const [assets, setAssets]           = useState([]);
    const [searchTerm, setSearchTerm]   = useState('');
    const [showModal, setShowModal]     = useState(false);
    const [modalMode, setModalMode]     = useState('create');
    const [currentAsset, setCurrentAsset] = useState(null);
    const [sortConfig, setSortConfig]   = useState({ key: '', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading]     = useState(true);
    const [error, setError]             = useState(null);
    const [paginationRange, setPaginationRange] = useState([]);
    const [successMessage, setSuccessMessage]   = useState(null);

    // Filters
    const [filters, setFilters] = useState({
        asset_type: '', brand: '', model: '', status: '', location: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [assetToDelete, setAssetToDelete]     = useState(null);

    // Assignment modal
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [assetToAssign, setAssetToAssign]             = useState(null);

    const PAGINATION_DISPLAY = 5;
    const rowsPerPage = 10;

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------
    const handleSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    /**
     * FIX: compare against raw backend values, not translated strings.
     * Previously this was: ASSIGNABLE_STATUSES.includes(t('available')) etc.
     */
    const canAssignAsset = (asset) => ASSIGNABLE_STATUSES.includes(asset.status);

    // -----------------------------------------------------------------------
    // Filter options derived from live asset data
    // -----------------------------------------------------------------------
    const filterOptions = useMemo(() => {
        const predefined = {
            asset_types: ['Laptop', 'Desktop', 'Monitor', 'Phone', 'Tablet', 'Printer', 'Scanner', 'Server', 'Other'],
            brands:   ['Dell', 'HP', 'Lenovo', 'Apple', 'Acer', 'ASUS', 'MSI', 'Samsung', 'LG', 'Canon', 'Epson', 'Cisco'],
            statuses: Object.keys(STATUS_BADGE_VARIANT),
            locations: [],
        };

        const dynamic = {
            asset_types: [...new Set(assets.map(a => a.asset_type).filter(Boolean))].sort(),
            brands:      [...new Set(assets.map(a => a.brand).filter(Boolean))].sort(),
            models:      [...new Set(assets.map(a => a.model).filter(Boolean))].sort(),
            statuses:    [...new Set(assets.map(a => a.status).filter(Boolean))].sort(),
            locations:   [...new Set(assets.map(a => a.location).filter(Boolean))].sort(),
        };

        const merge = (a, b) => [...new Set([...a, ...b])].sort();

        return {
            asset_types: merge(predefined.asset_types, dynamic.asset_types),
            brands:      merge(predefined.brands, dynamic.brands),
            models:      dynamic.models,
            statuses:    merge(predefined.statuses, dynamic.statuses),
            locations:   dynamic.locations,
        };
    }, [assets]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setFilters({ asset_type: '', brand: '', model: '', status: '', location: '' });
        setSearchTerm('');
        setCurrentPage(1);
    };

    const hasActiveFilters = useMemo(
        () => Object.values(filters).some(v => v !== '') || searchTerm !== '',
        [filters, searchTerm]
    );

    const activeFilterCount = useMemo(
        () => Object.values(filters).filter(v => v !== '').length + (searchTerm ? 1 : 0),
        [filters, searchTerm]
    );

    // -----------------------------------------------------------------------
    // Excel export
    // -----------------------------------------------------------------------
    const exportToExcel = () => {
        try {
            setIsLoading(true);
            const dataToExport = filteredAndSortedAssets.map(asset => ({
                [t('assets.assetType')]:            asset.asset_type || '',
                [t('assets.brand')]:                asset.brand || '',
                [t('assets.model')]:                asset.model || '',
                [t('assets.serialNumber')]:         asset.serial_number || '',
                [t('assetForm.imei')]:              asset.imei || '',
                [t('assets.status')]:               t(getStatusTranslationKey(asset.status)) || '',
                [t('assetForm.acquisitionDate')]:   asset.acquisition_date || '',
                [t('assetForm.price')]:             asset.price || 0,
                [t('assetForm.custodianEmail')]:    asset.custodian_email || '',
                [t('assetForm.phoneContact')]:      asset.phone_contact || '',
                [t('assetForm.office')]:            asset.office || '',
                [t('assets.location')]:             asset.location || '',
                [t('assetForm.detailsInformation')]: asset.details_information || '',
            }));

            const wb  = XLSX.utils.book_new();
            const ws  = XLSX.utils.json_to_sheet(dataToExport);
            const headers = Object.keys(dataToExport[0] || {});
            ws['!cols'] = headers.map((h, i) => {
                let max = h.length;
                dataToExport.forEach(row => { max = Math.max(max, String(row[h] || '').length); });
                return { width: Math.min(max + 2, 50) };
            });
            XLSX.utils.book_append_sheet(wb, ws, t('navigation.assets'));
            XLSX.writeFile(wb, `assets_export_${new Date().toISOString().split('T')[0]}.xlsx`);
            handleSuccess(t('messages.exportedToExcel'));
        } catch (err) {
            console.error(err);
            setError(t('failedToExportToExcel'));
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (viewMode === 'assets') {
            fetchAssets(true);
            if (users.length === 0) fetchUsers();
        } else if (viewMode === 'users') {
            fetchUsers();
        }
    }, [viewMode]);

    useEffect(() => {
        if (selectedUser?.email) fetchUserAssets(selectedUser.email);
    }, [selectedUser]);

    useEffect(() => {
        calculatePaginationRange();
    }, [currentPage, assets?.length, users?.length, userAssets?.length, searchTerm, sortConfig, viewMode, filters]);

    const fetchAssets = async (showLoader = true) => {
        try {
            if (showLoader) setIsLoading(true);
            setError(null);

            let data;
            try {
                data = await assetService.getAssets();
            } catch {
                const res = await axios.get(`${BASE_URL}/assets/`, getAuthConfig());
                data = res.data;
            }

            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else {
                for (const key of ['results', 'data', 'assets', 'items', 'records']) {
                    if (Array.isArray(data?.[key])) { list = data[key]; break; }
                }
            }
            setAssets(list);
        } catch (err) {
            const status = err.response?.status;
            const msg =
                status === 401 ? t('messages.authenticationFailed') :
                    status === 403 ? t('messages.accessDenied') :
                        status === 404 ? t('messages.endpointNotFound') :
                            err.response?.data?.detail || err.message || t('messages.failedToFetchAssets');
            setError(msg);
            setAssets([]);
        } finally {
            if (showLoader) setIsLoading(false);
        }
    };

    const handleManualRefresh = async () => {
        await fetchAssets(true);
        handleSuccess(t('messages.listRefreshed'));
    };

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const token = localStorage.getItem('accessToken');
            const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

            // GET /users/ is paginated server-side (defaults to 100, capped at
            // 1000 per request), so a single unparameterised call silently
            // truncates the list once there are more than 100 users. Page
            // through skip/limit until a page comes back short of the page
            // size so the assignment dropdown always has everyone.
            const PAGE_SIZE = 1000;
            let skip = 0;
            let allUsers = [];

            while (true) {
                const res = await fetch(`${BASE_URL}/users/?skip=${skip}&limit=${PAGE_SIZE}`, {
                    headers: authHeaders
                });
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                const page = (await res.json()) || [];
                allUsers = allUsers.concat(page);

                if (page.length < PAGE_SIZE) break;
                skip += PAGE_SIZE;
            }

            setUsers(allUsers);
        } catch (err) {
            setError(t('messages.failedToFetchUsers'));
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUserAssets = async (custodianEmail) => {
        // Clear stale data immediately so the previous user's equipment
        // never shows while the new fetch is in-flight.
        setUserAssets([]);
        setIsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('accessToken');
            const authHeaders = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            };

            // Assets are linked to a user via Asset.custodian_email — this is
            // the same field the Assets tab already displays as "Custodian",
            // so querying by it here keeps both tabs in sync with a single
            // source of truth. GET /assets/custodian/{email} is defined in
            // asset_router.py.
            const res = await fetch(
                `${BASE_URL}/assets/custodian/${encodeURIComponent(custodianEmail)}`,
                { headers: authHeaders }
            );

            let data;
            if (res.status === 404) {
                // No assets currently assigned to this user — not an error
                data = [];
            } else if (!res.ok) {
                throw new Error(`${res.status} ${res.statusText}`);
            } else {
                data = await res.json();
            }

            setUserAssets(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('fetchUserAssets error:', err);
            setError(t('messages.failedToFetchUserEquipment'));
            setUserAssets([]);
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Pagination
    // -----------------------------------------------------------------------
    const calculatePaginationRange = () => {
        let total = 0;
        if (viewMode === 'assets')      total = filteredAndSortedAssets?.length || 0;
        else if (viewMode === 'users')  total = filteredAndSortedUsers?.length || 0;
        else                            total = filteredAndSortedUserAssets?.length || 0;

        const pages = Math.ceil(total / rowsPerPage);
        let start = Math.max(1, currentPage - Math.floor(PAGINATION_DISPLAY / 2));
        let end   = Math.min(pages, start + PAGINATION_DISPLAY - 1);
        if (end - start + 1 < PAGINATION_DISPLAY) start = Math.max(1, end - PAGINATION_DISPLAY + 1);
        setPaginationRange(Array.from({ length: end - start + 1 }, (_, i) => start + i));
    };

    // -----------------------------------------------------------------------
    // CSV import
    // -----------------------------------------------------------------------

    /**
     * Resolves a field value from a CSV row by trying multiple possible header
     * names. Matching strategy (in order):
     *   1. Exact match
     *   2. Case-insensitive match
     *   3. Normalized match — strips spaces, underscores, hyphens, then lowercases
     *      so "Type of Asset" ~ "typeofasset" ~ "asset_type" ~ "AssetType"
     *      and "Aquisition Date" ~ "acquisitiondate" ~ "acquisition_date"
     */
    const resolveField = (row, candidates) => {
        const normalize = (s) => s.toLowerCase().replace(/[\s_\-]+/g, '');

        // Build lookup tables once per row
        const exactKeys   = new Set(Object.keys(row));
        const lowerRow    = {};
        const normalRow   = {};
        Object.keys(row).forEach(k => {
            lowerRow[k.toLowerCase().trim()] = row[k];
            normalRow[normalize(k)]          = row[k];
        });

        for (const key of candidates) {
            if (!key) continue;
            // 1. Exact
            if (exactKeys.has(key) && row[key] !== '') return row[key];
            // 2. Case-insensitive
            const lk = key.toLowerCase().trim();
            if (lowerRow[lk] !== undefined && lowerRow[lk] !== '') return lowerRow[lk];
            // 3. Normalized (word-order and separator agnostic)
            const nk = normalize(key);
            if (normalRow[nk] !== undefined && normalRow[nk] !== '') return normalRow[nk];
        }
        return '';
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setIsLoading(true);
        setError(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    if (!results.data || results.data.length === 0) {
                        throw new Error(t('messages.noValidAssetsInCsv'));
                    }

                    // Log detected headers to help diagnose future mismatches
                    console.log('CSV headers detected:', Object.keys(results.data[0] || {}));

                    const formatted = results.data.map(row => ({
                        asset_type: resolveField(row, [
                            // Exact headers seen in exported CSV
                            'Type of Asset', 'Asset Type',
                            // snake_case / other variants
                            'asset_type', 'AssetType', 'Type',
                            t('assets.assetType'),
                        ]),
                        brand: resolveField(row, [
                            'Brand', 'brand', 'Make',
                            t('assets.brand'),
                        ]),
                        model: resolveField(row, [
                            'Model', 'model',
                            t('assets.model'),
                        ]),
                        serial_number: resolveField(row, [
                            'Serial Number', 'serial_number', 'SerialNumber',
                            'Serial', 'SN',
                            t('assets.serialNumber'),
                        ]),
                        imei: resolveField(row, [
                            'IMEI', 'imei',
                            t('assetForm.imei'),
                        ]),
                        acquisition_date: resolveField(row, [
                            'Aquisition Date', 'Acquisition Date',
                            'acquisition_date', 'AcquisitionDate',
                            'Purchase Date', 'Date',
                            t('assetForm.acquisitionDate'),
                        ]) || null,
                        price: parseFloat(resolveField(row, [
                            'Price', 'price', 'Cost',
                            t('assetForm.price'),
                        ])) || 0,
                        custodian_email: resolveField(row, [
                            'Custodian Email', 'custodian_email', 'CustodianEmail',
                            'Custodian', 'Email',
                            t('assetForm.custodianEmail'),
                        ]),
                        status: resolveField(row, [
                            'Status', 'status',
                            t('assets.status'),
                        ]) || 'Available',
                        details_information: resolveField(row, [
                            'Details Information', 'details_information', 'Details',
                            'Notes', 'Description',
                            t('assetForm.detailsInformation'),
                        ]),
                        phone_contact: resolveField(row, [
                            'Phone Contact', 'phone_contact', 'Phone',
                            t('assetForm.phoneContact'),
                        ]),
                        office: resolveField(row, [
                            'Office', 'office',
                            t('assetForm.office'),
                        ]),
                        location: resolveField(row, [
                            'Location', 'location',
                            t('assets.location'),
                        ]),
                    })).filter(a => a.asset_type && a.brand && a.model && a.serial_number);

                    if (formatted.length === 0) {
                        // Give a more helpful error showing what headers were found
                        const found = Object.keys(results.data[0] || {}).join(', ');
                        throw new Error(
                            `${t('messages.noValidAssetsInCsv')}. ` +
                            `Headers found: ${found}. ` +
                            `Required: Asset Type, Brand, Model, Serial Number.`
                        );
                    }

                    let response;
                    try {
                        // Try service layer first, fall back to direct axios call
                        if (assetService.bulkCreateAssets) {
                            response = await assetService.bulkCreateAssets(formatted);
                        } else {
                            response = (await axios.post(
                                `${BASE_URL}/assets/bulk`,
                                formatted,
                                getAuthConfig()
                            )).data;
                        }
                    } catch {
                        // Service failed — try direct endpoint
                        response = (await axios.post(
                            `${BASE_URL}/assets/bulk`,
                            formatted,
                            getAuthConfig()
                        )).data;
                    }

                    if (Array.isArray(response)) {
                        // Legacy shape from assetService.bulkCreateAssets, if it
                        // hasn't been updated to the new {created, skipped} shape.
                        setAssets(prev => [...prev, ...response]);
                        handleSuccess(t('messages.assetsImportedSuccess', { count: response.length }));
                    } else if (response && Array.isArray(response.created)) {
                        setAssets(prev => [...prev, ...response.created]);
                        if (response.skipped_count > 0) {
                            const skippedSerials = response.skipped.map(s => s.serial_number).join(', ');
                            setError(
                                `${response.skipped_count} row(s) skipped — duplicate serial number(s): ${skippedSerials}`
                            );
                        }
                        handleSuccess(t('messages.assetsImportedSuccess', { count: response.created_count }));
                    } else {
                        await fetchAssets(false);
                        handleSuccess(t('messages.assetsImportedSuccess', { count: formatted.length }));
                    }
                } catch (err) {
                    setError(err.message || t('messages.csvProcessingError'));
                } finally {
                    setIsLoading(false);
                }
            },
            error: (err) => {
                setError(`${t('messages.csvReadingError')}: ${err.message}`);
                setIsLoading(false);
            },
        });
        event.target.value = '';
    };

    // -----------------------------------------------------------------------
    // Create / update asset
    // -----------------------------------------------------------------------
    const handleSubmit = async (formData) => {
        try {
            setError(null);
            setIsLoading(true);
            const cleaned = {
                ...formData,
                acquisition_date: formData.acquisition_date || null,
                price: formData.price ? parseFloat(formData.price) : 0,
            };

            if (modalMode === 'create') {
                let result;
                try { result = await assetService.createAsset(cleaned); }
                catch { result = (await axios.post(`${BASE_URL}/assets/`, cleaned, getAuthConfig())).data; }
                if (result) setAssets(prev => [...prev, result]);
                else await fetchAssets(false);
                handleSuccess(t('messages.assetCreatedSuccess'));

            } else if (modalMode === 'edit') {
                let result;
                try { result = await assetService.updateAsset(currentAsset.id, cleaned); }
                catch { result = (await axios.put(`${BASE_URL}/assets/${currentAsset.id}/`, cleaned, getAuthConfig())).data; }
                setAssets(prev => prev.map(a => a.id === currentAsset.id ? (result || { ...a, ...cleaned }) : a));
                handleSuccess(t('messages.assetUpdatedSuccess'));
            }

            setShowModal(false);
            setCurrentAsset(null);
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.message
                || (typeof err.response?.data === 'string' ? err.response.data : null)
                || err.message || t('messages.failedToCreateAsset');
            setError(detail);
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Delete asset
    // -----------------------------------------------------------------------
    const confirmDelete = (asset) => { setAssetToDelete(asset); setShowDeleteModal(true); };

    const handleDelete = async () => {
        if (!assetToDelete) return;
        try {
            setError(null);
            setIsLoading(true);
            try { await assetService.deleteAsset(assetToDelete.id); }
            catch { await axios.delete(`${BASE_URL}/assets/${assetToDelete.id}/`, getAuthConfig()); }
            setAssets(prev => prev.filter(a => a.id !== assetToDelete.id));
            handleSuccess(t('messages.assetDeletedSuccess'));
        } catch {
            setError(t('messages.failedToDeleteAsset'));
        } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
            setAssetToDelete(null);
        }
    };

    // -----------------------------------------------------------------------
    // Assign asset
    // -----------------------------------------------------------------------
    const handleAssignAsset = (asset) => {
        setAssetToAssign(asset);
        setShowAssignmentModal(true);
    };

    const handleAssignmentConfirm = async (assignmentData) => {
        try {
            setError(null);
            setIsLoading(true);

            // Use a distinct name to avoid shadowing the selectedUser state variable.
            // Shadowing it would prevent the useEffect from re-firing when the
            // assigned user is the same as the currently viewed user.
            const assignedToUser = users.find(u => String(u.id) === String(assignmentData.user_id));

            const payload = {
                asset_id:              assetToAssign.id,
                user_id:               assignmentData.user_id,
                delivery_type:         assignmentData.delivery_type,
                device_condition:      assignmentData.device_condition,
                date_assigned:         assignmentData.date_assigned,
                delivered_by:          assignmentData.delivered_by,
                recipient_signature:   assignmentData.recipient_signature   || '',
                head_of_ict_full_name: assignmentData.head_of_ict_full_name || '',
                head_ict_signature:    assignmentData.head_ict_signature    || '',
                custodian_email:       assignedToUser?.email || '',
            };

            // POST /equipment/assign-from-asset is the ONLY endpoint that creates
            // an EquipmentAssignment row (which is what the Users tab displays).
            // We call axios directly — NOT via assetService — because the service
            // method routes to PUT /assets/{id}/assign which only updates the Asset
            // status and never inserts into equipment_assignment.
            await axios.post(
                `${BASE_URL}/equipment/assign-from-asset`,
                payload,
                getAuthConfig()
            );

            // Update the asset row in local state immediately
            setAssets(prev => prev.map(a =>
                a.id === assetToAssign.id
                    ? { ...a, status: 'Assigned', custodian_email: assignedToUser?.email || '' }
                    : a
            ));

            // If the Users tab is already open on the assigned user, refresh their
            // equipment list so the new row appears without requiring a manual reload.
            if (selectedUser && String(selectedUser.id) === String(assignmentData.user_id)) {
                await fetchUserAssets(selectedUser.email);
            }

            handleSuccess(t('messages.assetAssignedSuccess'));
            setShowAssignmentModal(false);
            setAssetToAssign(null);

        } catch (err) {
            const msg = err.response?.data?.detail || err.message || t('messages.failedToAssignAsset');
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Sort / filter / paginate
    // -----------------------------------------------------------------------
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleUserClick = (user) => {
        // Clear previous user's equipment so stale rows never show
        setUserAssets([]);
        setSelectedUser(user);
        setViewMode('userAssets');
        setCurrentPage(1);
    };

    const handleBackToUsers = () => {
        setViewMode('users');
        setSelectedUser(null);
        setUserAssets([]);   // clean up so next selection starts fresh
        setCurrentPage(1);
    };

    const filteredAndSortedAssets = useMemo(() => {
        const lower = searchTerm.toLowerCase();
        return assets
            .filter(a => {
                const matchesSearch = !lower || Object.values(a).some(v => String(v).toLowerCase().includes(lower));
                return (
                    matchesSearch &&
                    (!filters.asset_type || a.asset_type === filters.asset_type) &&
                    (!filters.brand      || a.brand      === filters.brand)      &&
                    (!filters.model      || a.model      === filters.model)      &&
                    (!filters.status     || a.status     === filters.status)     &&
                    (!filters.location   || a.location   === filters.location)
                );
            })
            .sort((a, b) => {
                if (!sortConfig.key) return 0;
                const d = sortConfig.direction === 'asc' ? 1 : -1;
                return (a[sortConfig.key] ?? '').toString().toLowerCase()
                    .localeCompare((b[sortConfig.key] ?? '').toString().toLowerCase()) * d;
            });
    }, [assets, searchTerm, sortConfig, filters]);

    const filteredAndSortedUsers = useMemo(() => {
        const lower = searchTerm.toLowerCase();
        return users
            .filter(u => Object.values(u).some(v => String(v).toLowerCase().includes(lower)))
            .sort((a, b) => {
                if (!sortConfig.key) return 0;
                const d = sortConfig.direction === 'asc' ? 1 : -1;
                return (a[sortConfig.key] ?? '').toString().toLowerCase()
                    .localeCompare((b[sortConfig.key] ?? '').toString().toLowerCase()) * d;
            });
    }, [users, searchTerm, sortConfig]);

    const filteredAndSortedUserAssets = useMemo(() => {
        const lower = searchTerm.toLowerCase();
        return userAssets
            .filter(a => Object.values(a).some(v => String(v).toLowerCase().includes(lower)))
            .sort((a, b) => {
                if (!sortConfig.key) return 0;
                const d = sortConfig.direction === 'asc' ? 1 : -1;
                return (a[sortConfig.key] ?? '').toString().toLowerCase()
                    .localeCompare((b[sortConfig.key] ?? '').toString().toLowerCase()) * d;
            });
    }, [userAssets, searchTerm, sortConfig]);

    const paginatedItems = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        if (viewMode === 'assets')     return filteredAndSortedAssets.slice(start, start + rowsPerPage);
        if (viewMode === 'users')      return filteredAndSortedUsers.slice(start, start + rowsPerPage);
        if (viewMode === 'userAssets') return filteredAndSortedUserAssets.slice(start, start + rowsPerPage);
        return [];
    }, [filteredAndSortedAssets, filteredAndSortedUsers, filteredAndSortedUserAssets, currentPage, viewMode]);

    const totalPages = useMemo(() => {
        if (viewMode === 'assets')     return Math.ceil(filteredAndSortedAssets.length / rowsPerPage);
        if (viewMode === 'users')      return Math.ceil(filteredAndSortedUsers.length / rowsPerPage);
        if (viewMode === 'userAssets') return Math.ceil(filteredAndSortedUserAssets.length / rowsPerPage);
        return 0;
    }, [filteredAndSortedAssets.length, filteredAndSortedUsers.length, filteredAndSortedUserAssets.length, viewMode]);

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------
    const renderFilters = () => {
        if (viewMode !== 'assets') return null;
        return (
            <Card className="mb-3">
                <Card.Header
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowFilters(v => !v)}
                >
                    <div className="d-flex align-items-center">
                        <FaFilter className="me-2" />
                        <span>{t('assets.filters')}</span>
                        {activeFilterCount > 0 && (
                            <Badge bg="primary" className="ms-2">{activeFilterCount}</Badge>
                        )}
                    </div>
                    <div className="d-flex align-items-center">
                        {hasActiveFilters && (
                            <Button
                                variant="outline-secondary" size="sm" className="me-2"
                                onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}
                            >
                                <FaTimes className="me-1" /> {t('assets.clearAll')}
                            </Button>
                        )}
                        <span>{showFilters ? '▲' : '▼'}</span>
                    </div>
                </Card.Header>

                {showFilters && (
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label>{t('common.search')}</Form.Label>
                                    <Form.Control
                                        placeholder={t('assets.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        disabled={isLoading}
                                    />
                                </Form.Group>
                            </Col>
                            {[
                                { key: 'asset_type', label: t('assets.assetType'), allLabel: t('assets.allTypes'),     opts: filterOptions.asset_types },
                                { key: 'brand',      label: t('assets.brand'),     allLabel: t('assets.allBrands'),    opts: filterOptions.brands },
                                { key: 'model',      label: t('assets.model'),     allLabel: t('assets.allModels'),    opts: filterOptions.models },
                                { key: 'status',     label: t('assets.status'),    allLabel: t('assets.allStatuses'),  opts: filterOptions.statuses },
                                { key: 'location',   label: t('assets.location'),  allLabel: t('assets.allLocations'), opts: filterOptions.locations },
                            ].map(({ key, label, allLabel, opts }) => (
                                <Col md={6} lg={2} key={key}>
                                    <Form.Group>
                                        <Form.Label>{label}</Form.Label>
                                        <Form.Select
                                            value={filters[key]}
                                            onChange={e => handleFilterChange(key, e.target.value)}
                                            disabled={isLoading}
                                        >
                                            <option value="">{allLabel}</option>
                                            {opts.map(opt => (
                                                <option key={opt} value={opt}>
                                                    {key === 'status' ? t(getStatusTranslationKey(opt)) : opt}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>

                        <Row className="mt-3">
                            <Col>
                                <div className="text-muted">
                                    {t('assets.showingResults', {
                                        filtered: filteredAndSortedAssets.length,
                                        total: assets.length,
                                    })}
                                    {hasActiveFilters && (
                                        <span className="text-primary"> {t('assets.filtered')}</span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                )}
            </Card>
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        return (
            <Pagination className="justify-content-center mt-3">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev  onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
                {currentPage > PAGINATION_DISPLAY && (
                    <>
                        <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
                        {currentPage > PAGINATION_DISPLAY + 1 && <Pagination.Ellipsis />}
                    </>
                )}
                {paginationRange.map(p => (
                    <Pagination.Item key={p} active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                ))}
                {currentPage < totalPages - PAGINATION_DISPLAY && (
                    <>
                        <Pagination.Ellipsis />
                        <Pagination.Item onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>
                    </>
                )}
                <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
        );
    };

    const renderTable = () => {
        if (isLoading) return <div className="text-center p-4">{t('assets.loading')}</div>;

        // ---- Assets table ----
        if (viewMode === 'assets') {
            if (assets.length === 0) {
                return <div className="text-center p-4">{error || t('assets.noAssetsFound')}</div>;
            }
            if (filteredAndSortedAssets.length === 0) {
                return (
                    <div className="text-center p-4">
                        <p>{t('assets.noAssetsMatchFilters')}</p>
                        <Button variant="outline-primary" onClick={clearAllFilters}>
                            <FaTimes className="me-2" /> {t('assets.clearAll')}
                        </Button>
                    </div>
                );
            }

            return (
                <div className="table-responsive">
                    <Table striped bordered hover>
                        <thead>
                        <tr>
                            {[
                                ['asset_type',     t('assets.assetType')],
                                ['brand',          t('assets.brand')],
                                ['model',          t('assets.model')],
                                ['serial_number',  t('assets.serialNumber')],
                                ['status',         t('assets.status')],
                                ['custodian_email',t('assets.custodian')],
                                ['location',       t('assets.location')],
                            ].map(([key, label]) => (
                                <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer' }}>
                                    {label} <FaSort />
                                </th>
                            ))}
                            <th>{t('assets.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedItems.map((asset) => (
                            <tr key={asset.id}>
                                <td>{asset.asset_type}</td>
                                <td>{asset.brand}</td>
                                <td>{asset.model}</td>
                                <td>{asset.serial_number}</td>
                                <td>
                                    {/* FIX: use raw status for variant lookup, not translated string */}
                                    <Badge bg={getStatusBadgeVariant(asset.status)}>
                                        {t(getStatusTranslationKey(asset.status))}
                                    </Badge>
                                </td>
                                <td>{asset.custodian_email || '-'}</td>
                                <td>{asset.location || '-'}</td>
                                <td>
                                    <div className="d-flex gap-1 flex-wrap">

                                        {/* View */}
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            title={t('assets.view')}
                                            onClick={() => {
                                                setModalMode('view');
                                                setCurrentAsset(asset);
                                                setShowModal(true);
                                            }}
                                        >
                                            <FaEye className="me-1" />
                                            {t('assets.view')}
                                        </Button>

                                        {/* Edit */}
                                        <Button
                                            variant="outline-warning"
                                            size="sm"
                                            title={t('assets.edit')}
                                            onClick={() => {
                                                setModalMode('edit');
                                                setCurrentAsset(asset);
                                                setShowModal(true);
                                            }}
                                        >
                                            <FaEdit className="me-1" />
                                            {t('assets.edit')}
                                        </Button>

                                        {/*
                                             * Assign button — only shown when the asset's raw status
                                             * is in ASSIGNABLE_STATUSES. Previously canAssignAsset()
                                             * compared against t('available') which always failed.
                                             */}
                                        {canAssignAsset(asset) && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                title={t('assets.assign')}
                                                onClick={() => handleAssignAsset(asset)}
                                            >
                                                <FaUserPlus className="me-1" />
                                                {t('assets.assign')}
                                            </Button>
                                        )}

                                        {/* Delete */}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            title={t('assets.delete')}
                                            onClick={() => confirmDelete(asset)}
                                        >
                                            <FaTrash className="me-1" />
                                            {t('assets.delete')}
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        // ---- Users table ----
        if (viewMode === 'users') {
            if (users.length === 0) {
                return <div className="text-center p-4">{error || t('users.noUsersFound')}</div>;
            }
            return (
                <div className="table-responsive">
                    <Table striped bordered hover>
                        <thead>
                        <tr>
                            {[
                                ['last_name',          t('users.name')],
                                ['email',              t('users.email')],
                                ['username',           t('users.username')],
                                ['role.name',          t('users.role')],
                                ['employment.function',t('users.function')],
                            ].map(([key, label]) => (
                                <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer' }}>
                                    {label} <FaSort />
                                </th>
                            ))}
                            <th>{t('assets.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedItems.map((user) => (
                            <tr key={user.id}>
                                <td>{`${user.first_name} ${user.last_name}`}</td>
                                <td>{user.email}</td>
                                <td>{user.username}</td>
                                <td>{user.role?.name || 'N/A'}</td>
                                <td>{user.employment?.function || 'N/A'}</td>
                                <td>
                                    <Button variant="primary" size="sm" onClick={() => handleUserClick(user)}>
                                        {t('users.viewEquipment')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            );
        }

        // ---- User assets table ----
        if (viewMode === 'userAssets') {
            // Guard: isLoading is checked at the top of renderTable(), but we
            // also clear userAssets before each fetch, so we must not show the
            // empty-state message while a fetch is in-flight.
            if (isLoading) {
                return <div className="text-center p-4">{t('assets.loading')}</div>;
            }
            if (userAssets.length === 0) {
                return <div className="text-center p-4">{error || t('equipment.noEquipmentFound')}</div>;
            }
            return (
                <div className="table-responsive">
                    <Table striped bordered hover>
                        <thead>
                        <tr>
                            {[
                                ['asset_type',    t('assets.assetType')],
                                ['brand',         t('assets.brand')],
                                ['model',         t('assets.model')],
                                ['serial_number', t('assets.serialNumber')],
                                ['status',        t('assets.status')],
                                ['location',      t('assets.location')],
                            ].map(([key, label]) => (
                                <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer' }}>
                                    {label} <FaSort />
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedItems.map((asset) => (
                            <tr key={asset.id}>
                                <td>{asset.asset_type}</td>
                                <td>{asset.brand}</td>
                                <td>{asset.model}</td>
                                <td>{asset.serial_number}</td>
                                <td>{t(getStatusTranslationKey(asset.status))}</td>
                                <td>{asset.location || 'N/A'}</td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            );
        }
    };

    // -----------------------------------------------------------------------
    // Main render
    // -----------------------------------------------------------------------
    return (
        <Container className="py-4">
            <Card>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Header row */}
                    <Row className="mb-4 align-items-center">
                        <Col>
                            <h2>{t('assets.title')}</h2>
                        </Col>
                        <Col xs="auto">
                            {viewMode === 'assets' && (
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleManualRefresh}
                                        disabled={isLoading}
                                        title={t('assets.refresh')}
                                    >
                                        <FaSync className={`me-2 ${isLoading ? 'fa-spin' : ''}`} />
                                        {t('assets.refresh')}
                                    </Button>
                                    <Button
                                        variant="outline-success"
                                        onClick={exportToExcel}
                                        disabled={isLoading || filteredAndSortedAssets.length === 0}
                                        title={t('assets.exportToExcel')}
                                    >
                                        <FaFileExcel className="me-2" />
                                        {t('assets.exportToExcel')}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        disabled={isLoading}
                                        onClick={() => { setModalMode('create'); setCurrentAsset(null); setShowModal(true); }}
                                    >
                                        <FaPlus className="me-2" /> {t('assets.addAsset')}
                                    </Button>
                                    <Form.Group>
                                        <Form.Label className="btn btn-outline-primary mb-0">
                                            <FaUpload className="me-2" /> {t('assets.importCsv')}
                                            <Form.Control
                                                type="file"
                                                accept=".csv"
                                                className="d-none"
                                                onChange={handleFileUpload}
                                                disabled={isLoading}
                                            />
                                        </Form.Label>
                                    </Form.Group>
                                </div>
                            )}
                        </Col>
                    </Row>

                    {/* Tabs */}
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link
                                active={viewMode === 'assets'}
                                onClick={() => { setViewMode('assets'); setCurrentPage(1); }}
                            >
                                {t('navigation.assets')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                active={viewMode === 'users' || viewMode === 'userAssets'}
                                onClick={() => { setViewMode('users'); setSelectedUser(null); setCurrentPage(1); }}
                            >
                                {t('navigation.users')}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {/* User assets back button + info */}
                    {viewMode === 'userAssets' && selectedUser && (
                        <div className="mb-3">
                            <Button variant="outline-secondary" onClick={handleBackToUsers}>
                                <FaArrowLeft className="me-2" /> {t('users.backToUsers')}
                            </Button>
                            <h4 className="mt-3">
                                {t('users.equipmentAssignedTo', {
                                    name: `${selectedUser.first_name} ${selectedUser.last_name}`
                                })}
                            </h4>
                            <p>
                                <strong>{t('users.email')}:</strong> {selectedUser.email} |
                                <strong> {t('users.username')}:</strong> {selectedUser.username} |
                                <strong> {t('users.role')}:</strong> {selectedUser.role?.name || 'N/A'} |
                                <strong> {t('users.function')}:</strong> {selectedUser.employment?.function || 'N/A'} |
                                <strong> {t('users.phone')}:</strong> {selectedUser.contact?.mobile_phone || 'N/A'}
                            </p>
                        </div>
                    )}

                    {renderFilters()}

                    {/* Search bar for non-assets views */}
                    {viewMode !== 'assets' && (
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Control
                                    placeholder={`${t('common.search')} ${
                                        viewMode === 'users'
                                            ? t('navigation.users').toLowerCase()
                                            : t('equipment.category').toLowerCase()
                                    }...`}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    disabled={isLoading}
                                />
                            </Col>
                        </Row>
                    )}

                    {renderTable()}
                    {renderPagination()}
                </Card.Body>
            </Card>

            {/* Asset form modal */}
            <AssetForm
                show={showModal}
                onHide={() => setShowModal(false)}
                mode={modalMode}
                asset={currentAsset}
                onSubmit={handleSubmit}
            />

            {/* Assignment modal */}
            <AssetAssignmentModal
                show={showAssignmentModal}
                onHide={() => { setShowAssignmentModal(false); setAssetToAssign(null); }}
                asset={assetToAssign}
                users={users}
                onAssign={handleAssignmentConfirm}
            />

            {/* Delete confirmation modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('assets.deleteConfirmTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {assetToDelete && (
                        <>
                            <p>{t('assets.deleteConfirmMessage')}</p>
                            <p className="mb-1"><strong>{t('assets.assetType')}:</strong> {assetToDelete.asset_type}</p>
                            <p className="mb-1"><strong>{t('assets.brand')}:</strong> {assetToDelete.brand}</p>
                            <p className="mb-1"><strong>{t('assets.model')}:</strong> {assetToDelete.model}</p>
                            <p className="mb-0"><strong>{t('assets.serialNumber')}:</strong> {assetToDelete.serial_number}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('assets.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        <FaTrash className="me-2" /> {t('assets.delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AssetPage;